import { Link } from "react-router-dom";
import { ORGANS, CONHECA_SEU_CORPO, VISAO_INTEGRATIVA } from "@/data/organs";
import { elementColor } from "@/data/theme";
import { PageHeader, ElementBadge, SourceTag } from "@/components/common";
import { ArrowRight, Compass, Sparkles, Heart, Activity, CheckCircle2 } from "lucide-react";

export default function Organs() {
  return (
    <div className="space-y-12">
      {/* PÁGINA FÍSICA 5 / IMPRESSA 3: CONHEÇA SEU CORPO */}
      <section className="space-y-6" data-testid="section-conheca-seu-corpo">
        <PageHeader
          eyebrow="Visão geral do corpo"
          title={CONHECA_SEU_CORPO.titulo}
          subtitle={CONHECA_SEU_CORPO.subtitulo}
        >
          <div className="mt-3">
            <SourceTag source={CONHECA_SEU_CORPO.source} />
          </div>
        </PageHeader>

        <div className="grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-7 space-y-4 text-base text-muted-foreground leading-relaxed">
            <p className="font-serif text-lg text-foreground font-medium">
              {CONHECA_SEU_CORPO.texto1}
            </p>
            <p>{CONHECA_SEU_CORPO.texto2}</p>

            <div className="rounded-2xl border border-gold/30 bg-gold/10 p-5 text-sm text-foreground">
              <span className="font-semibold uppercase tracking-wider text-xs text-gold block mb-1">
                Lembre-se
              </span>
              <p className="italic font-serif">{CONHECA_SEU_CORPO.lembrete}</p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-border bg-card p-6 sm:p-7 space-y-4">
              <div className="flex items-center gap-2 text-gold">
                <Compass className="h-5 w-5" />
                <h3 className="font-serif text-lg font-semibold text-foreground">
                  {CONHECA_SEU_CORPO.comoLerMapa.titulo}
                </h3>
              </div>
              <ul className="space-y-3">
                {CONHECA_SEU_CORPO.comoLerMapa.pontos.map((ponto, i) => (
                  <li key={i} className="flex items-start gap-3 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold text-xs font-semibold mt-0.5">
                      {i + 1}
                    </span>
                    <span>{ponto}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PÁGINA FÍSICA 6 / IMPRESSA 4: MAPA DOS 12 ÓRGÃOS E VÍSCERAS DA MTC */}
      <section className="space-y-8 border-t border-border pt-10" data-testid="section-mapa-12-orgaos">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl sm:text-3xl font-serif font-medium text-foreground">
              Mapa dos 12 Órgãos e Vísceras da MTC
            </h2>
            <p className="mt-1 text-sm text-muted-foreground max-w-2xl leading-relaxed">
              {VISAO_INTEGRATIVA.subtitulo}
            </p>
          </div>
          <SourceTag source={VISAO_INTEGRATIVA.source} />
        </div>

        {/* Grade com os 12 órgãos e suas microdescrições resumidas fiéis ao mapa */}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {ORGANS.map((o) => {
            const c = elementColor(o.elemento);
            return (
              <Link
                key={o.id}
                to={`/orgaos/${o.id}`}
                data-testid={`organ-card-${o.id}`}
                className="group relative flex flex-col justify-between overflow-hidden rounded-3xl border border-border bg-card p-6 sm:p-7 transition-all hover:-translate-y-1 hover:border-gold/40"
              >
                <div>
                  <span
                    className="absolute right-5 top-5 font-serif text-5xl font-semibold opacity-15"
                    style={{ color: c.primary }}
                  >
                    {String(o.number).padStart(2, "0")}
                  </span>
                  <ElementBadge elementKey={o.elemento} label={o.elementoLabel} />
                  <h3 className="mt-4 text-2xl font-serif font-medium text-foreground">{o.name}</h3>
                  <p className="mt-1 text-xs italic text-muted-foreground">{o.subtitle}</p>

                  {/* Microdescrição resumida própria do mapa da página 6 */}
                  {o.resumoMapa && (
                    <p className="mt-3 text-xs text-foreground/90 font-medium leading-relaxed bg-secondary/40 rounded-xl p-3 border border-border/50">
                      {o.resumoMapa}
                    </p>
                  )}
                </div>

                <div className="mt-5 border-t border-border/50 pt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span><span className="font-medium text-foreground">Sabor:</span> {o.sabor}</span>
                  <span><span className="font-medium text-foreground">Emoção:</span> {o.emocao}</span>
                  <span><span className="font-medium text-foreground">Horário:</span> {o.relogio.periodo}</span>
                </div>
              </Link>
            );
          })}
        </div>

        {/* A VISÃO INTEGRATIVA (Página física 6 / impressa 4) */}
        <div className="rounded-3xl border border-gold/30 bg-card p-6 sm:p-8 space-y-6" data-testid="section-visao-integrativa">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-border/60 pb-4">
            <h3 className="text-xl sm:text-2xl font-serif font-medium text-foreground flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-gold" /> {VISAO_INTEGRATIVA.titulo}
            </h3>
            <span className="text-xs text-muted-foreground uppercase tracking-wider">Unidade funcional da MTC</span>
          </div>

          <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">
            {VISAO_INTEGRATIVA.descricao}
          </p>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 pt-2">
            {VISAO_INTEGRATIVA.etapas.map((etapa, i) => (
              <div
                key={i}
                className="relative flex items-center gap-3 rounded-2xl bg-secondary/50 border border-border p-4 transition-colors"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold/20 text-gold text-xs font-semibold">
                  {i + 1}
                </span>
                <p className="text-sm font-medium text-foreground">{etapa}</p>
                {i < VISAO_INTEGRATIVA.etapas.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gold/60 z-10" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
